/*
 * Copyright (C) 2014 Spanning Cloud Apps LLC.  All rights reserved.
 */
import { ValidationUtils } from 'spanalog/lib/utils/ValidationUtils';
import './preview.tpl.html';

angular
  .module('bulkRestore.preview', ['ngSanitize', 'restore.columntext', 'services.bulkrestore', 'services.error'])
  // 2 different 'content tabs' to view in modal, 'Fields' and 'Children'
  .constant(
    'ContentType',
    Object.freeze({
      FIELDS: 'fields',
      CHILDREN: 'children'
    })
  )
  // The columns we can sort on in the field table
  .constant(
    'FieldSortKeys',
    Object.freeze({
      FIELD: 'label',
      BACKED_UP_VALUE: 'value',
      CURRENT_VALUE: 'currentValue'
    })
  )
  // The columns we can sort on in the child table
  .constant(
    'ChildSortKeys',
    Object.freeze({
      NAME: 'sfName',
      TYPE: 'sobjectName',
      DEPTH: 'depth',
      LAST_MODIFIED: 'lastModified'
    })
  )
  /**
   * Controller for the bulkRestore.confirm template.
   */
  .controller('PreviewRecordCtrl', [
    '$scope',
    '$rootScope',
    '$filter',
    '$uibModalInstance',
    '$q',
    'batchUtils',
    'pagingUtils',
    'bulkRestoreData',
    'recordIndex',
    'BulkRestoreService',
    'ChildSortKeys',
    'ContentType',
    'ErrorService',
    'FieldSortKeys',
    'MomentFormats',
    'TYPES',
    'BULK_RESTORE_EVENTS',
    function (
      $scope,
      $rootScope,
      $filter,
      $uibModalInstance,
      $q,
      batchUtils,
      pagingUtils,
      bulkRestoreData,
      recordIndex,
      BulkRestoreService,
      ChildSortKeys,
      ContentType,
      ErrorService,
      FieldSortKeys,
      MomentFormats,
      TYPES,
      BULK_RESTORE_EVENTS
    ) {
      var i18n = $filter('i18n');
      var ordinal = $filter('ordinal');
      var deletedStr = i18n('DELETED');
      var updatedStr = i18n('UPDATED');
      var pagingInfo = pagingUtils.pagingInfo.bulkRestoreAllRecords;
      var record = bulkRestoreData.getPageObjectRecords()[recordIndex];
      var oldPage = 0;
      var model;

      // Publish functions
      $scope.cancelPreview = cancelPreview;
      $scope.getChildLevel = getChildLevel;
      $scope.getChildModifiedDate = getChildModifiedDate;
      $scope.getHeaderClass = getHeaderClass;
      $scope.getObjectType = getObjectType;
      $scope.getObjectTypeIconClass = getObjectTypeIconClass;
      $scope.getRecordId = getRecordId;
      $scope.getRecordBatchId = getRecordBatchId;
      $scope.getRecordName = getRecordName;
      $scope.getRecordStateText = getRecordStateText;
      $scope.hasNextRecord = hasNextRecord;
      $scope.hasPreviousRecord = hasPreviousRecord;
      $scope.isFieldValid = isFieldValid;
      $scope.isViewingChildren = isViewingChildren;
      $scope.isViewingFields = isViewingFields;
      $scope.loadChildren = loadChildren;
      $scope.loadNextRecord = loadNextRecord;
      $scope.loadPreview = loadPreview;
      $scope.loadPreviousRecord = loadPreviousRecord;
      $scope.shouldShowChildren = shouldShowChildren;
      $scope.shouldShowField = shouldShowField;
      $scope.sortChildren = sortChildren;
      $scope.sortFields = sortFields;
      $scope.viewChildren = viewChildren;
      $scope.viewFields = viewFields;

      $scope.ChildSortKeys = ChildSortKeys;
      $scope.ContentType = ContentType;
      $scope.FieldSortKeys = FieldSortKeys;

      // Store data in a model object rather than the scope itself for easier watching
      $scope.model = model = {
        childCount: 0,
        children: undefined,
        childSortAscending: true,
        childSortKey: ChildSortKeys.NAME,
        fieldList: undefined,
        fieldSortAscending: true,
        fieldSortKey: FieldSortKeys.FIELD,
        lastModDateStr: '',
        modifiedCount: 0,
        record: record,
        recordIndex: recordIndex,
        selectedContent: ContentType.FIELDS,
        showDiffOnly: true
      };

      watchRecordList();

      // load initial preview
      loadPreview();

      /**
       * Watches for changes to the record list.
       */
      function watchRecordList() {
        $scope.$watch(
          function () {
            return bulkRestoreData.getPageObjectRecords();
          },
          function (newRecords) {
            if (oldPage > 0) {
              // Went to prev page
              if (oldPage > pagingInfo.currentPage) {
                model.recordIndex = newRecords.length - 1;
              } else {
                model.recordIndex = 0;
              }

              model.record = record = newRecords[model.recordIndex];
              $scope.loadPreview();
              oldPage = 0;
            }
          }
        );
      }

      /**
       * Loads the preview details for record.
       *
       * @returns {Promise} a promise that resolves when the details are loaded.
       */
      function loadPreview() {
        const sobjectRecordId = getRecordId();
        const sobjectRecordBatchId = getRecordBatchId();

        $rootScope.showLoadingMask = true;

        model.children = undefined;
        model.modifiedCount = 0;
        model.lastModDateStr = '';
        model.fieldList = undefined;

        return $q
          .all([
            BulkRestoreService.getObjectRecordPreview(sobjectRecordId, sobjectRecordBatchId),
            BulkRestoreService.getObjectRecordDiff(sobjectRecordId, sobjectRecordBatchId),
            loadChildren()
          ])
          .then(function (results) {
            var fieldList = results[0];
            var diffResult = results[1];
            var diffs = diffResult.diffs;

            record.isNotFound = diffResult.notFound;

            if (diffs) {
              model.modifiedCount = diffs.length;
            }

            fieldList = _.map(fieldList, function (field) {
              var diffObj = _.findWhere(diffs, { name: field.name.toLowerCase() });
              if (diffObj) {
                field.currentValue = diffObj.currentValue;
              } else {
                field.currentValue = record.isNotFound ? null : field.value;
              }

              return field;
            });

            model.lastModDateStr = moment(record.lastModified).format(MomentFormats.BATCH);
            model.fieldList = fieldList;
          })
          .catch(function (err) {
            ErrorService.report(err, i18n('DELETED_ITEMS_PREVIEW_FAILED'));
          })
          .finally(function () {
            $rootScope.showLoadingMask = false;
          });
      }

      /**
       * Determines whether a field has a real value.
       *
       * @param {object} field - The field to inspect
       * @returns {boolean} true if the field is valid
       */
      function isFieldValid(field) {
        var fieldValue = field.value || field.currentValue;
        return !(fieldValue === undefined || fieldValue === null || fieldValue === '');
      }

      /**
       * Loads the child list for a deleted record.
       *
       * @returns {Promise} a promise that resolves when the list is loaded.
       */
      function loadChildren() {
        if (record.isDeleted && !model.children) {
          return BulkRestoreService.getObjectRecordPreviewChildren(getRecordId(), getRecordBatchId())
            .then(function (children) {
              model.children = children;
              model.childCount = _.size(children);
            })
            .catch(function (err) {
              ErrorService.report(err, i18n('DELETED_ITEMS_PREVIEW_DESCENDANTS_FAILED'));
            });
        } else {
          return $q.when();
        }
      }

      /**
       * Returns the class to be used to style a header in the field or child list.
       *
       * @param {string} sortKey - The sort key used to identify the header.
       * @returns {string} The class to use.
       */
      function getHeaderClass(sortKey) {
        var headerClass = '';

        if (isViewingFields()) {
          if (model.fieldSortKey === sortKey) {
            if (model.fieldSortAscending) {
              headerClass = 'sbsf-log-header-asc sca-icon-down-black';
            } else {
              headerClass = 'sbsf-log-header-desc sca-icon-down-black';
            }
          }
        } else if (isViewingChildren()) {
          if (model.childSortKey === sortKey) {
            if (model.childSortAscending) {
              headerClass = 'sbsf-log-header-asc sca-icon-down-black';
            } else {
              headerClass = 'sbsf-log-header-desc sca-icon-down-black';
            }
          }
        }

        return headerClass;
      }

      /**
       * Returned the formatted dateTime string representing the last modified date of a child record.
       *
       * @param {object} child - The child record.
       * @returns {string} - A formatted date string.
       */
      function getChildModifiedDate(child) {
        return moment(child.lastModified).format(MomentFormats.BATCH);
      }

      /**
       * Returns the ordinal value of the depth of a child record.
       *
       * @param {object} child - The child to be inspected.
       * @returns {string} An integer with ordinal added.
       */
      function getChildLevel(child) {
        return ordinal(child.depth);
      }

      /**
       * Loads the previous record into the view.
       */
      function loadPreviousRecord() {
        if (hasPreviousRecord()) {
          model.selectedContent = ContentType.FIELDS;

          if (model.recordIndex - 1 < 0) {
            oldPage = pagingInfo.currentPage;
            $rootScope.$emit(BULK_RESTORE_EVENTS.PAGING_EVENT, pagingInfo.currentPage - 1);
          } else {
            model.record = record = bulkRestoreData.getPageObjectRecords()[--model.recordIndex];
            return $scope.loadPreview();
          }
        }

        return $q.when();
      }

      /**
       * Loads the next record into the view.
       */
      function loadNextRecord() {
        if (hasNextRecord()) {
          model.selectedContent = ContentType.FIELDS;

          if (model.recordIndex + 1 >= bulkRestoreData.getPageObjectRecordCount()) {
            oldPage = pagingInfo.currentPage;
            $rootScope.$emit(BULK_RESTORE_EVENTS.PAGING_EVENT, pagingInfo.currentPage + 1);
          } else {
            model.record = record = bulkRestoreData.getPageObjectRecords()[++model.recordIndex];
            return $scope.loadPreview();
          }
        }

        return $q.when();
      }

      /**
       * Determines whether a field should be displayed.
       *
       * @param {object} field - The field to inspect
       * @returns {boolean} true if the field should be displayed.
       */
      function shouldShowField(field) {
        return model.showDiffOnly ? field.value !== field.currentValue : true;
      }

      /**
       * Handler to close modal.
       */
      function cancelPreview() {
        $uibModalInstance.dismiss('cancel');
      }

      /**
       * Determines the type of the current object record.
       *
       * @returns {string} the type of the current object record.
       */
      function getObjectType() {
        return bulkRestoreData.isRestoreFromCsv() ? record.sobjectType : bulkRestoreData.getSearchObjectType();
      }

      /**
       * Determines the CSS class for the object type icon.
       *
       * @returns {string} the CSS class for the object type icon.
       */
      function getObjectTypeIconClass() {
        return TYPES.typeToCls[getObjectType()];
      }

      /**
       * Returns the id of the current record.
       *
       * @returns {string | number} the id of the current record.
       */
      function getRecordId() {
        return record.recordId || record.id;
      }

      /**
       * Returns the id of the current record.
       *
       * @returns {string | number} the id of the current record.
       */
      function getRecordBatchId() {
        return record.csvVerifyId && record.sobjectRecordBatchId ? record.sobjectRecordBatchId : record.batchId;
      }

      /**
       * Returns the name of the current record.
       *
       * @returns {string} the name of the current record.
       */
      function getRecordName() {
        return record.sfName;
      }

      /**
       * Returns the text describing the state of the current record (deleted or updated).
       *
       * @returns {string} the text describing the state of the current record.
       */
      function getRecordStateText() {
        return BulkRestoreService.getRecordStateText(record, bulkRestoreData);
      }

      /**
       * Determines whether there is another record after the current one.
       *
       * @returns {boolean} true if there is another record after the current one.
       */
      function hasNextRecord() {
        return (
          pagingInfo.currentPage < pagingInfo.totalPages ||
          (pagingInfo.currentPage === pagingInfo.totalPages &&
            model.recordIndex < bulkRestoreData.getPageObjectRecordCount() - 1)
        );
      }

      /**
       * Determines whether there is another record before the current one.
       *
       * @returns {boolean} true if there is another record before the current one.
       */
      function hasPreviousRecord() {
        return pagingInfo.currentPage > 1 || (pagingInfo.currentPage === 1 && model.recordIndex > 0);
      }

      /**
       * Determines whether the child list is being displayed.
       *
       * @returns {boolean} true if the child list is being displayed.
       */
      function isViewingChildren() {
        return model.selectedContent === ContentType.CHILDREN;
      }

      /**
       * Determines whether the field list is being displayed.
       *
       * @returns {boolean} true if the field list is being displayed.
       */
      function isViewingFields() {
        return model.selectedContent === ContentType.FIELDS;
      }

      /**
       * Determines whether the child section of the modal should be visible.
       *
       * @returns {boolean} true if the child section of the modal should be visible.
       */
      function shouldShowChildren() {
        return record.isDeleted;
      }

      /**
       * Sorts the child list by the specified key.
       *
       * @param {string} sortKey - The sort key.
       */
      function sortChildren(sortKey) {
        ValidationUtils.assertIsEnumValue(sortKey, 'sortKey', ChildSortKeys);

        if (model.childSortKey !== sortKey) {
          // Start with ascending when we switch to a new column
          model.childSortKey = sortKey;
          model.childSortAscending = true;
        } else {
          // Toggle the direction when we stay on the same column
          model.childSortAscending = !model.childSortAscending;
        }
      }

      /**
       * Sorts the field list by the specified key.
       *
       * @param {string} sortKey - The sort key.
       */
      function sortFields(sortKey) {
        ValidationUtils.assertIsEnumValue(sortKey, 'sortKey', FieldSortKeys);

        if (model.fieldSortKey !== sortKey) {
          // Start with ascending when we switch to a new column
          model.fieldSortKey = sortKey;
          model.fieldSortAscending = true;
        } else {
          // Toggle the direction when we stay on the same column
          model.fieldSortAscending = !model.fieldSortAscending;
        }
      }

      /**
       * Displays the list of children.
       */
      function viewChildren() {
        model.selectedContent = ContentType.CHILDREN;
      }

      /**
       * Displays the list of fields.
       */
      function viewFields() {
        model.selectedContent = ContentType.FIELDS;
      }
    }
  ]);
